import React, { useMemo } from 'react'
import {
  AdvancedSettingsIcon,
  B1Battery,
  B1BatteryFull,
  B3Photovoltaic,
  D1Boiler,
  DynamicPriceIcon,
  I1ElectricalChargingStation,
  K1Proximitycard,
  SafetyLockIcon,
} from '@e3dc-react/icons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { FeatureElements, getHasElementNewContent, useHasNewFeature } from 'src/Libs/new-content'
import { useTypedSelector } from 'src/State/RootReducer'
import { makeStyles } from 'tss-react/mui'
import { SystemSettingsDataCardModel } from '../types'
import { getAdvancedSettingsDetailsRoute } from 'src/Libs/flow/advanced-settings/routes'
import { getEndUserSettingsRoutes } from 'src/Libs/flow/end-user-settings/general/utils/routes'
import { INSTALLER_PARTNER, SERVICE_LEVELS } from 'src/Configuration'
import { getStorageBatteriesSettingsRoute } from 'src/Libs/flow/battery-settings'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'

const useStyles = makeStyles()(() => ({
  icon: {
    fontSize: '52px',
  },
}))

const useFlowSystemSettingsOverviewCardConfigs = (): SystemSettingsDataCardModel[] => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const activeInstallation = useTypedSelector(state => state.storage.activeInstallation)
  const activeStorage = useTypedSelector(state => state.storage.activeStorage)
  const { currentFeatureContentVersion } = useHasNewFeature()
  const userData = useTypedSelector(state => state.user.userData)
  const isServiceInstallerUser = userData?.level ? [...INSTALLER_PARTNER, ...SERVICE_LEVELS].includes(userData.level) : false
  const {
    featureFlags: { batterySettingsEnabled },
    isFeatureFlagsLoading,
  } = useFeatureFlagsContext()

  const systemSettingsCardConfigs: SystemSettingsDataCardModel[] = useMemo(() => {
    const configs: SystemSettingsDataCardModel[] = [
      {
        title: t('framework.grid-and-system-protection-configuration'),
        description: t('systemSettings.grid-and-system-protection-configuration.pageDescription'),
        icon: <SafetyLockIcon fontSize={'inherit'} className={classes.icon} />,
        path: `/installation/${activeInstallation}/${activeStorage}/properties/grid-and-system-protection-configuration`,
        hasNewContent: getHasElementNewContent(
          currentFeatureContentVersion,
          FeatureElements.PropertyGridAndSystemProtectionConfigurationSideBarElement
        ),
        elementKey: FeatureElements.PropertyGridAndSystemProtectionConfigurationSideBarElement,
        disabled: !activeStorage,
        hide: !isServiceInstallerUser,
      },
      {
        title: t('framework.battery'),
        description: t('systemSettings.battery.description'),
        icon: <B1BatteryFull className={classes.icon} />,
        path: getStorageBatteriesSettingsRoute(activeInstallation?.toString(), activeStorage),
        hasNewContent: getHasElementNewContent(currentFeatureContentVersion, FeatureElements.PropertyStorageBatterySettingsElement),
        elementKey: FeatureElements.PropertyStorageBatterySettingsElement,
        accessibleWhenInstallationOffline: true,
        disabled: !activeStorage,
        hide: !isServiceInstallerUser || isFeatureFlagsLoading || !batterySettingsEnabled,
      },
      {
        title: t('framework.advanced-settings'),
        description: t('systemSettings.advanced-settings.pageDescription'),
        icon: <AdvancedSettingsIcon fontSize={'inherit'} className={classes.icon} />,
        path: getAdvancedSettingsDetailsRoute(activeInstallation?.toString(), activeStorage),
        hasNewContent: getHasElementNewContent(currentFeatureContentVersion, FeatureElements.PropertyAdvancedSettingsSideBarElement),
        elementKey: FeatureElements.PropertyAdvancedSettingsSideBarElement,
        accessibleWhenInstallationOffline: true,
      },
      {
        title: t('framework.tariffs'),
        description: t('systemSettings.tariffs.pageDescription'),
        icon: <DynamicPriceIcon className={classes.icon} />,
        path: `/installation/${activeInstallation}/${activeStorage}/properties/tariffs`,
        hasNewContent: getHasElementNewContent(currentFeatureContentVersion, FeatureElements.PropertyTariffsSideBarElement),
        elementKey: FeatureElements.PropertyTariffsSideBarElement,
        disabled: !activeStorage,
      },
      {
        title: t('framework.end-user.solar'),
        description: t('systemSettings.end-user.solar.description'),
        icon: <B3Photovoltaic className={classes.icon} />,
        path: getEndUserSettingsRoutes('solarPriority', activeInstallation?.toString(), activeStorage),
        hasNewContent: getHasElementNewContent(currentFeatureContentVersion, FeatureElements.PropertyEndUserSettingsSolarElement),
        elementKey: FeatureElements.PropertyEndUserSettingsSolarElement,
        accessibleWhenInstallationOffline: true,
        disabled: !activeStorage,
      },
      {
        title: t('framework.end-user.badges'),
        description: t('systemSettings.end-user.badges.description'),
        icon: <K1Proximitycard className={classes.icon} />,
        path: getEndUserSettingsRoutes('badges', activeInstallation?.toString(), activeStorage),
        hasNewContent: getHasElementNewContent(currentFeatureContentVersion, FeatureElements.PropertyEndUserSettingsBadgesElement),
        elementKey: FeatureElements.PropertyEndUserSettingsBadgesElement,
        accessibleWhenInstallationOffline: true,
        disabled: !activeStorage,
      },
      {
        title: t('framework.end-user.wallbox'),
        description: t('systemSettings.end-user.wallbox.description'),
        icon: <I1ElectricalChargingStation className={classes.icon} />,
        path: getEndUserSettingsRoutes('wallbox', activeInstallation?.toString(), activeStorage),
        hasNewContent: getHasElementNewContent(currentFeatureContentVersion, FeatureElements.PropertyEndUserSettingsWallboxElement),
        elementKey: FeatureElements.PropertyEndUserSettingsWallboxElement,
        accessibleWhenInstallationOffline: true,
        disabled: !activeStorage,
      },
      {
        title: t('framework.end-user.storage'),
        description: t('systemSettings.end-user.storage.description'),
        icon: <B1Battery className={classes.icon} />,
        path: getEndUserSettingsRoutes('storage', activeInstallation?.toString(), activeStorage),
        hasNewContent: getHasElementNewContent(currentFeatureContentVersion, FeatureElements.PropertyEndUserSettingsStorageEnergyManagementElement),
        elementKey: FeatureElements.PropertyEndUserSettingsStorageEnergyManagementElement,
        accessibleWhenInstallationOffline: true,
        disabled: !activeStorage,
      },
      {
        title: t('framework.end-user.boiler'),
        description: t('systemSettings.end-user.boiler.description'),
        icon: <D1Boiler className={classes.icon} />,
        path: getEndUserSettingsRoutes('boiler', activeInstallation?.toString(), activeStorage),
        hasNewContent: getHasElementNewContent(currentFeatureContentVersion, FeatureElements.PropertyEndUserSettingsBoilerElement),
        elementKey: FeatureElements.PropertyEndUserSettingsBoilerElement,
        accessibleWhenInstallationOffline: true,
        disabled: !activeStorage,
      },
    ]

    return configs.filter(config => !config.hide)
  }, [
    t,
    classes.icon,
    activeInstallation,
    activeStorage,
    currentFeatureContentVersion,
    isServiceInstallerUser,
    isFeatureFlagsLoading,
    batterySettingsEnabled,
  ])

  return systemSettingsCardConfigs
}

export default useFlowSystemSettingsOverviewCardConfigs
